import axiosInstance from './axiosInstance';

export interface ReportStats {
  totalOrders: number;
  totalRoutes: number;
  totalAuditLogs: number;
  generatedReports: number;
  lastGenerated: string;
}

class ReportsService {
  private baseEndpoint = '/reports';

  /**
   * Export orders report (CSV/PDF)
   */
  async exportOrdersReport(format: 'csv' | 'pdf' = 'csv'): Promise<Blob> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/orders`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting orders report:', error);
      throw error;
    }
  }

  /**
   * Export routes report (CSV/PDF)
   */
  async exportRoutesReport(format: 'csv' | 'pdf' = 'csv'): Promise<Blob> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/routes`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting routes report:', error);
      throw error;
    }
  }

  /**
   * Export audit report (CSV/PDF)
   */
  async exportAuditReport(format: 'csv' | 'pdf' = 'csv'): Promise<Blob> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/audit`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting audit report:', error);
      throw error;
    }
  }

  /**
   * Get report statistics
   */
  async getReportStats(): Promise<ReportStats> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/stats`);
      return response.data?.data || this.getEmptyStats();
    } catch (error) {
      console.error('Error fetching report stats:', error);
      return this.getEmptyStats();
    }
  }

  private getEmptyStats(): ReportStats {
    return {
      totalOrders: 0,
      totalRoutes: 0,
      totalAuditLogs: 0,
      generatedReports: 0,
      lastGenerated: new Date().toISOString()
    };
  }
}

// Export singleton instance
export const reportsService = new ReportsService();

// Export convenience functions
export const exportOrdersReport = (format: 'csv' | 'pdf' = 'csv') => 
  reportsService.exportOrdersReport(format);
export const exportRoutesReport = (format: 'csv' | 'pdf' = 'csv') => 
  reportsService.exportRoutesReport(format);
export const exportAuditReport = (format: 'csv' | 'pdf' = 'csv') => 
  reportsService.exportAuditReport(format);
export const getReportStats = () => reportsService.getReportStats();
